import { useAuthStore } from '../stores/auth';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';
const REQUEST_TIMEOUT = 30000; // 30 seconds

interface RequestOptions extends RequestInit {
  requireAuth?: boolean;
  timeout?: number;
}

// User-friendly error messages
const ERROR_MESSAGES: Record<number, string> = {
  400: 'Richiesta non valida. Verifica i dati inseriti.',
  401: 'Sessione scaduta. Effettua nuovamente l\'accesso.',
  403: 'Non hai i permessi per questa operazione.',
  404: 'Risorsa non trovata.',
  409: 'I dati sono in conflitto con quelli esistenti.',
  422: 'I dati inseriti non sono validi.',
  429: 'Troppe richieste. Riprova tra qualche minuto.',
  500: 'Si e verificato un errore. Riprova piu tardi.',
  502: 'Servizio temporaneamente non disponibile.',
  503: 'Servizio in manutenzione. Riprova piu tardi.',
};

const NETWORK_ERROR_MESSAGE = 'Impossibile connettersi al server. Verifica la connessione internet.';
const TIMEOUT_ERROR_MESSAGE = 'La richiesta ha impiegato troppo tempo. Riprova.';
const UNKNOWN_ERROR_MESSAGE = 'Si e verificato un errore imprevisto. Riprova piu tardi.';

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<T> {
    const { requireAuth = false, timeout = REQUEST_TIMEOUT, ...fetchOptions } = options;
    const url = `${this.baseUrl}${endpoint}`;

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...fetchOptions.headers,
    };

    if (requireAuth) {
      const token = useAuthStore.getState().accessToken;
      if (token) {
        (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
      }
    }

    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        ...fetchOptions,
        headers,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.status === 401 && requireAuth) {
        // Try to refresh token
        const refreshed = await this.refreshToken();
        if (refreshed) {
          const newToken = useAuthStore.getState().accessToken;
          (headers as Record<string, string>)['Authorization'] = `Bearer ${newToken}`;
          const retryResponse = await fetch(url, { ...fetchOptions, headers });
          if (!retryResponse.ok) {
            throw new ApiError(
              retryResponse.status,
              this.getUserFriendlyMessage(retryResponse.status)
            );
          }
          return retryResponse.json();
        }
        useAuthStore.getState().logout();
        throw new ApiError(401, ERROR_MESSAGES[401]);
      }

      if (!response.ok) {
        throw new ApiError(
          response.status,
          this.getUserFriendlyMessage(response.status)
        );
      }

      return response.json();
    } catch (error) {
      clearTimeout(timeoutId);

      // Handle abort (timeout)
      if (error instanceof Error && error.name === 'AbortError') {
        throw new ApiError(408, TIMEOUT_ERROR_MESSAGE);
      }

      // Handle network errors
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new ApiError(0, NETWORK_ERROR_MESSAGE);
      }

      // Re-throw ApiError as-is
      if (error instanceof ApiError) {
        throw error;
      }

      // Unknown error
      throw new ApiError(500, UNKNOWN_ERROR_MESSAGE);
    }
  }

  private getUserFriendlyMessage(status: number): string {
    return ERROR_MESSAGES[status] || UNKNOWN_ERROR_MESSAGE;
  }

  private async refreshToken(): Promise<boolean> {
    const refreshToken = useAuthStore.getState().refreshToken;
    if (!refreshToken) return false;

    try {
      const response = await fetch(`${this.baseUrl}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) return false;

      const data = await response.json();
      useAuthStore.getState().setTokens(data.accessToken, data.refreshToken);
      return true;
    } catch {
      return false;
    }
  }

  // Auth
  async register(email: string, password: string) {
    return this.request<AuthResponse>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async login(email: string, password: string) {
    return this.request<AuthResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async logout() {
    const refreshToken = useAuthStore.getState().refreshToken;
    return this.request('/api/auth/logout', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify({ refreshToken }),
    });
  }

  async getMe() {
    return this.request<{ user: User }>('/api/auth/me', {
      requireAuth: true,
    });
  }

  // Listings
  async getListings(params: ListingsParams) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.set(key, String(value));
      }
    });
    const response = await this.request<ListingsResponse>(`/api/listings?${searchParams}`, {
      requireAuth: false,
    });
    response.data = response.data.map(parseListingImages);
    return response;
  }

  async getListing(id: string) {
    const listing = await this.request<Listing>(`/api/listings/${id}`, {
      requireAuth: false,
    });
    return parseListingImages(listing);
  }

  async getMapListings(params: MapListingsParams) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.set(key, String(value));
      }
    });
    const response = await this.request<{ listings: MapListing[] }>(`/api/listings/map?${searchParams}`);
    // Filter out listings with invalid coordinates
    response.listings = response.listings.filter(
      (listing) =>
        listing.latitude !== null &&
        listing.longitude !== null &&
        !isNaN(listing.latitude) &&
        !isNaN(listing.longitude) &&
        listing.latitude >= -90 && listing.latitude <= 90 &&
        listing.longitude >= -180 && listing.longitude <= 180
    );
    return response;
  }

  // Live search from Immobiliare API (real-time data)
  async getLiveListings(params: LiveListingsParams) {
    const searchParams = new URLSearchParams();
    searchParams.set('city', params.city);

    // Contract type mapping
    if (params.contractType) {
      searchParams.set('contractType', params.contractType === 'SALE' ? 'vendita' : 'affitto');
    }

    // Pagination
    if (params.page) searchParams.set('page', String(params.page));
    if (params.limit) searchParams.set('limit', String(params.limit));

    // Price filters
    if (params.minPrice) searchParams.set('minPrice', String(params.minPrice));
    if (params.maxPrice) searchParams.set('maxPrice', String(params.maxPrice));

    // Surface filters
    if (params.minSurface) searchParams.set('minSurface', String(params.minSurface));
    if (params.maxSurface) searchParams.set('maxSurface', String(params.maxSurface));

    // Room filters
    if (params.minRooms) searchParams.set('minRooms', String(params.minRooms));
    if (params.maxRooms) searchParams.set('maxRooms', String(params.maxRooms));

    // Bathroom filter
    if (params.bathrooms) searchParams.set('bathrooms', String(params.bathrooms));

    // Feature filters
    if (params.hasElevator) searchParams.set('hasElevator', '1');
    if (params.hasTerrace) searchParams.set('hasTerrace', '1');
    if (params.isFurnished) searchParams.set('isFurnished', '1');
    if (params.hasGarden) searchParams.set('hasGarden', '1');

    // Property condition filter (for sales)
    if (params.propertyCondition) searchParams.set('propertyCondition', params.propertyCondition);

    // Sort - default is 'date' for most recent
    if (params.sortBy) searchParams.set('sortBy', params.sortBy);

    const response = await this.request<ListingsResponse>(`/api/listings/live?${searchParams}`);
    response.data = response.data.map(parseListingImages);
    return response;
  }

  // Live map search - viewport-based search using both APIs
  async getLiveMapListings(params: LiveMapParams) {
    const searchParams = new URLSearchParams();
    searchParams.set('north', String(params.north));
    searchParams.set('south', String(params.south));
    searchParams.set('east', String(params.east));
    searchParams.set('west', String(params.west));

    // Map contract type: SALE → vendita, RENT → affitto, undefined → tutti (both)
    if (params.contractType === 'SALE') {
      searchParams.set('contractType', 'vendita');
    } else if (params.contractType === 'RENT') {
      searchParams.set('contractType', 'affitto');
    } else {
      searchParams.set('contractType', 'tutti');
    }
    if (params.minPrice) searchParams.set('minPrice', String(params.minPrice));
    if (params.maxPrice) searchParams.set('maxPrice', String(params.maxPrice));
    if (params.minSurface) searchParams.set('minSurface', String(params.minSurface));
    if (params.maxSurface) searchParams.set('maxSurface', String(params.maxSurface));
    if (params.minRooms) searchParams.set('minRooms', String(params.minRooms));
    if (params.maxRooms) searchParams.set('maxRooms', String(params.maxRooms));
    if (params.bathrooms) searchParams.set('bathrooms', String(params.bathrooms));
    if (params.hasElevator) searchParams.set('hasElevator', '1');
    if (params.hasTerrace) searchParams.set('hasTerrace', '1');
    if (params.isFurnished) searchParams.set('isFurnished', '1');
    if (params.hasGarden) searchParams.set('hasGarden', '1');
    if (params.propertyCondition) searchParams.set('propertyCondition', params.propertyCondition);
    if (params.sortBy) searchParams.set('sortBy', params.sortBy);

    const response = await this.request<ListingsResponse & { detectedCity?: string; searchDistance?: number }>(`/api/listings/live/map?${searchParams}`);
    response.data = response.data.map(parseListingImages);
    return response;
  }

  async getListingStats() {
    return this.request<ListingStats>('/api/listings/stats');
  }

  // Saved searches
  async getSavedSearches() {
    return this.request<{ searches: SavedSearch[] }>('/api/searches', {
      requireAuth: true,
    });
  }

  async createSavedSearch(data: CreateSavedSearchInput) {
    return this.request<{ search: SavedSearch }>('/api/searches', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify(data),
    });
  }

  async updateSavedSearch(id: string, data: Partial<CreateSavedSearchInput>) {
    return this.request<{ search: SavedSearch }>(`/api/searches/${id}`, {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify(data),
    });
  }

  async deleteSavedSearch(id: string) {
    return this.request(`/api/searches/${id}`, {
      method: 'DELETE',
      requireAuth: true,
    });
  }

  // Favorites
  async getFavorites() {
    const response = await this.request<{ favorites: FavoriteWithListing[] }>('/api/favorites', {
      requireAuth: true,
    });
    response.favorites = response.favorites.map(fav => ({
      ...fav,
      listing: parseListingImages(fav.listing),
    }));
    return response;
  }

  async addFavorite(listingId: string) {
    return this.request('/api/favorites', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify({ listingId }),
    });
  }

  async removeFavorite(listingId: string) {
    return this.request(`/api/favorites/${listingId}`, {
      method: 'DELETE',
      requireAuth: true,
    });
  }

  // Push notifications
  async getVapidKey() {
    return this.request<{ publicKey: string }>('/api/push/vapid-key');
  }

  async subscribePush(subscription: PushSubscriptionJSON) {
    return this.request('/api/push/subscribe', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify(subscription),
    });
  }

  async unsubscribePush() {
    return this.request('/api/push/unsubscribe', {
      method: 'DELETE',
      requireAuth: true,
    });
  }

  // Leads
  async getLeads(filters?: LeadFiltersParams) {
    const searchParams = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          searchParams.set(key, String(value));
        }
      });
    }
    return this.request<{ leads: Lead[]; total: number }>(`/api/leads?${searchParams}`, {
      requireAuth: true,
    });
  }

  async createLead(data: CreateLeadInput) {
    return this.request<{ lead: Lead }>('/api/leads', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify(data),
    });
  }

  async updateLead(id: string, data: Partial<CreateLeadInput & { status: LeadStatus }>) {
    return this.request<{ lead: Lead }>(`/api/leads/${id}`, {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify(data),
    });
  }

  async deleteLead(id: string) {
    return this.request(`/api/leads/${id}`, {
      method: 'DELETE',
      requireAuth: true,
    });
  }

  async addLeadNote(leadId: string, data: { text: string; isCallAttempt: boolean }) {
    return this.request<{ note: LeadNote }>(`/api/leads/${leadId}/notes`, {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify(data),
    });
  }

  async getLeadStats() {
    return this.request<{ stats: Record<string, number> }>('/api/leads/stats', {
      requireAuth: true,
    });
  }

  async extractContact(platform: string, externalId: string) {
    return this.request<{ contact: ContactInfo | null }>('/api/leads/extract-contact', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify({ platform, externalId }),
    });
  }
}

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = 'ApiError';
  }
}

// Types
export interface User {
  id: string;
  email: string;
}

export interface AuthResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
}

export interface ListingImage {
  url: string;
  label?: string | null;
}

export interface Listing {
  id: string;
  externalId: string;
  platform: 'IMMOBILIARE' | 'IDEALISTA' | 'CASA' | 'SUBITO';
  title: string;
  description?: string;
  price?: number;
  priceFormatted?: string;
  contractType: 'SALE' | 'RENT';
  propertyType: string;
  address?: string;
  city: string;
  province?: string;
  postalCode?: string;
  zone?: string;
  microzone?: string;
  latitude?: number;
  longitude?: number;
  surface?: number;
  rooms?: number;
  bathrooms?: number;
  floor?: string;
  energyClass?: string;
  // New detailed fields
  hasLift?: boolean;
  hasBalcony?: boolean;
  isFurnished?: boolean;
  features?: string[];
  agencyName?: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  propertyStatus?: string;
  heating?: string;
  // Images with labels
  images: (string | ListingImage)[];
  imageUrls?: string[];
  sourceUrl: string;
  publishedAt?: string;
  createdAt: string;
  updatedAt?: string;
  isFavorite?: boolean;
}

export interface MapListing {
  id: string;
  latitude: number;
  longitude: number;
  price: number | null;
  contractType: 'SALE' | 'RENT';
  propertyType: string;
  title: string;
  imageUrl?: string;
  surface?: number;
  rooms?: number;
  address?: string;
}

export interface ListingsParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  contractType?: string;
  propertyType?: string;
  platform?: string;
  city?: string;
  province?: string;
  minPrice?: number;
  maxPrice?: number;
  minSurface?: number;
  maxSurface?: number;
  minRooms?: number;
  maxRooms?: number;
  minBathrooms?: number;
  energyClass?: string;
}

export interface MapListingsParams extends ListingsParams {
  north: number;
  south: number;
  east: number;
  west: number;
}

export type LiveSortBy = 'date' | 'price' | 'priceAsc' | 'surface' | 'rooms' | 'relevance';

export interface LiveListingsParams {
  city: string;
  contractType?: string;
  page?: number;
  limit?: number;
  minPrice?: number;
  maxPrice?: number;
  minSurface?: number;
  maxSurface?: number;
  minRooms?: number;
  maxRooms?: number;
  bathrooms?: number;
  sortBy?: LiveSortBy;
  // Feature filters
  hasElevator?: boolean;
  hasTerrace?: boolean;
  isFurnished?: boolean;
  hasGarden?: boolean;
  // Property condition (for sales)
  propertyCondition?: 'nuovo' | 'ristrutturato' | 'buono' | 'da_ristrutturare';
}

export interface LiveMapParams {
  north: number;
  south: number;
  east: number;
  west: number;
  contractType?: string;
  minPrice?: number;
  maxPrice?: number;
  minSurface?: number;
  maxSurface?: number;
  minRooms?: number;
  maxRooms?: number;
  bathrooms?: number;
  sortBy?: LiveSortBy;
  hasElevator?: boolean;
  hasTerrace?: boolean;
  isFurnished?: boolean;
  hasGarden?: boolean;
  propertyCondition?: 'nuovo' | 'ristrutturato' | 'buono' | 'da_ristrutturare';
}

export interface ListingsResponse {
  data: Listing[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasMore: boolean;
  };
}

export interface ListingStats {
  total: number;
  byPlatform: Record<string, number>;
  byContractType: Record<string, number>;
  byPropertyType: Record<string, number>;
}

export interface AlertResult {
  id: string;
  externalId: string;
  platform: string;
  title: string;
  price: number | null;
  contractType: string;
  city: string;
  surface: number | null;
  rooms: number | null;
  bathrooms: number | null;
  sourceUrl: string;
  imageUrl?: string | null;
  createdAt?: string | null;
  foundAt: string;
}

export interface SavedSearch {
  id: string;
  name: string;
  filters: ListingsParams;
  notifyEnabled: boolean;
  lastNotifiedAt?: string;
  lastAlertResults?: AlertResult[];
  createdAt: string;
}

export interface CreateSavedSearchInput {
  name: string;
  filters: ListingsParams;
  notifyEnabled?: boolean;
}

// Lead types
export type LeadStatus = 'NEW' | 'CONTACTED' | 'INTERESTED' | 'NOT_INTERESTED' | 'CONVERTED';

export interface LeadNote {
  id: string;
  text: string;
  isCallAttempt: boolean;
  createdAt: string;
}

export interface Lead {
  id: string;
  name: string;
  phone?: string;
  email?: string;
  propertyAddress?: string;
  sourceUrl?: string;
  platform?: string;
  externalListingId?: string;
  listingTitle?: string;
  listingPrice?: number;
  listingImage?: string;
  savedSearchId?: string;
  savedSearch?: { name: string } | null;
  status: LeadStatus;
  callAttempts: number;
  notes: LeadNote[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateLeadInput {
  name: string;
  phone?: string;
  email?: string;
  propertyAddress?: string;
  sourceUrl?: string;
  platform?: string;
  externalListingId?: string;
  listingTitle?: string;
  listingPrice?: number;
  listingImage?: string;
  savedSearchId?: string;
}

export interface LeadFiltersParams {
  status?: LeadStatus;
  search?: string;
  savedSearchId?: string;
  page?: number;
  limit?: number;
}

export interface ContactInfo {
  name?: string;
  phone?: string;
  email?: string;
  agencyName?: string;
}

export interface FavoriteWithListing {
  id: string;
  listingId: string;
  createdAt: string;
  listing: Listing;
}

// Helper to parse listing images (backend returns JSON string)
function parseListingImages(listing: Listing): Listing {
  if (typeof listing.images === 'string') {
    try {
      listing.images = JSON.parse(listing.images);
    } catch {
      listing.images = [];
    }
  }
  if (!Array.isArray(listing.images)) {
    listing.images = [];
  }
  return listing;
}

export const api = new ApiClient(API_BASE_URL);
